import { TextInput } from '@frontend/components/basic-forms';
import { ActionModal } from '@frontend/components/modal';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage } from 'react-intl';

import { useLinkModal } from './link-modal.controller';

export interface LinkModalData {
    href: string;
    text: string;
    target: string | null;
}

export interface LinkModalProps {
    initialData: Partial<LinkModalData>;
    isEditing?: boolean;
    onConfirm(_data: LinkModalData): void;
    onCancel(): void;
    onUnlink(): void;
}

export const LinkModal = (props: LinkModalProps) => {
    const viewProps = useLinkModal(props);

    return (
        <ActionModal
            title={
                props.isEditing ? (
                    <FormattedMessage
                        id='content-detail.editor.link-modal.edit-title'
                        description='Title for the modal used to edit an existing link in notification content'
                        defaultMessage='Edit link'
                    />
                ) : (
                    <FormattedMessage
                        id='content-detail.editor.link-modal.title'
                        description='Title for the modal used to insert a link in notification content'
                        defaultMessage='Insert link'
                    />
                )
            }
            cancel={{ onCancel: props.onCancel, label: CommonMessage.BUTTONS.CANCEL }}
            confirm={{
                onConfirm: () =>
                    props.onConfirm({ href: viewProps.href, text: viewProps.text, target: viewProps.target === '_self' ? null : viewProps.target }),
                label: CommonMessage.BUTTONS.CONFIRM,
                disabled: !viewProps.href.trim() || !viewProps.text.trim()
            }}
            additionalButtons={
                viewProps.canUnlink
                    ? [
                          {
                              onClick: props.onUnlink,
                              label: (
                                  <FormattedMessage
                                      id='content-detail.editor.link-modal.unlink'
                                      description='Button label for removing a link mark from selected notification content text'
                                      defaultMessage='Unlink'
                                  />
                              )
                          }
                      ]
                    : undefined
            }>
            <div className='flex w-full flex-col gap-2'>
                <TextInput
                    label={
                        <FormattedMessage
                            id='content-detail.editor.link-modal.href'
                            description='Label for the link URL field in the notification content link modal'
                            defaultMessage='URL'
                        />
                    }
                    value={viewProps.href}
                    onChange={viewProps.setHref}
                />
                <TextInput
                    label={
                        <FormattedMessage
                            id='content-detail.editor.link-modal.text'
                            description='Label for the visible link text field in the notification content link modal'
                            defaultMessage='Text'
                        />
                    }
                    value={viewProps.text}
                    onChange={viewProps.setText}
                />
            </div>
        </ActionModal>
    );
};
